import { applyPlay, pickShell, type DriveUpdate } from './drive';
import { catchChance, idealBand, powerAccuracy, powerBonus, resolvePass, throwQuality, type PassResult, type PowerBand } from './passing';
import { PlaySim, type Vec2 } from './playSim';
import { RacWorld, type RacEvent } from './rac';
import { Rng } from './rng';
import { pocketTime, pressurePenalty } from './running';
import type { Tunables } from './tunables';
import type { CatchTiming, DriveState, Formation, Play, Player, Shell } from './types';

export interface PlayContext {
  drive: DriveState;
  play: Play;
  flip: boolean;
  offense: Player[];
  defense: Player[];
  defenseFormation: Formation;
  seed: number;
  t: Tunables;
}

export interface LivePlay {
  ctx: PlayContext;
  shell: Shell;
  sim: PlaySim;
  rng: Rng;
  /** Seconds after the snap when the pocket breaks; the sack comes sackAfterSeconds later. */
  pocket: number;
  sackTime: number;
  qbSlot: number;
}

export const SHELL_NAMES: Record<Shell, string> = { man: 'Man', cover2: 'Cover 2', cover3: 'Cover 3' };

/** Snap: picks the coverage and builds the live play. All randomness for the play comes from ctx.seed. */
export function snap(ctx: PlayContext): LivePlay {
  const rng = new Rng(ctx.seed);
  const shell = pickShell(ctx.drive, rng, ctx.t);
  const ol = ctx.offense.filter((p) => p.pos === 'OL');
  const dl = ctx.defense.filter((p) => p.pos === 'DL');
  const pocket = ctx.play.type === 'run' ? Infinity : pocketTime(ol, dl, ctx.t);
  const sim = new PlaySim({
    play: ctx.play,
    defenseFormation: ctx.defenseFormation,
    offense: ctx.offense,
    defense: ctx.defense,
    shell,
    flip: ctx.flip,
    rng,
    t: ctx.t,
    pocket,
    ballOn: ctx.drive.ballOn,
  });
  return { ctx, shell, sim, rng, pocket, sackTime: pocket + ctx.t.pocket.sackAfterSeconds, qbSlot: sim.qbSlot };
}

export interface ThrowPlan {
  receiverIndex: number;
  throwTime: number;
  flightSeconds: number;
  /** Ball-relative release and catch points. */
  from: Vec2;
  to: Vec2;
  distance: number;
  /** 0 to 100, or undefined for a tap throw at the band centre. */
  power: number | undefined;
  band: PowerBand;
  sep: number;
  pressure: number;
}

/** QB position at a time after the snap (he drops, then sets). */
export function qbPosition(live: LivePlay, time: number, out: Vec2): Vec2 {
  const m = live.ctx.t.movement;
  const start = live.sim.offenseStart[live.qbSlot]!;
  out.x = start.x;
  out.y = start.y - m.qbDropYards * Math.min(1, Math.max(0, time / m.qbDropSeconds));
  return out;
}

/** Plans a throw: where the receiver will be when the ball arrives, flight time, the band and the pressure. No rolls yet. */
export function planThrow(live: LivePlay, receiverIndex: number, throwTime: number, power?: number): ThrowPlan {
  const { sim, ctx } = live;
  const t = ctx.t;
  if (!sim.receivers[receiverIndex]) throw new Error(`No receiver ${receiverIndex}`);
  const from = qbPosition(live, throwTime, { x: 0, y: 0 });
  const to: Vec2 = { x: 0, y: 0 };
  let flight = t.throw.minFlightSeconds;
  // Lead the receiver: a few fixed-point steps converge on the meeting point.
  for (let i = 0; i < 4; i++) {
    sim.receiverPos(receiverIndex, throwTime + flight, to);
    const dist = Math.hypot(to.x - from.x, to.y - from.y);
    flight = Math.max(t.throw.minFlightSeconds, dist / ballSpeed(power, dist, t));
  }
  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  return {
    receiverIndex,
    throwTime,
    flightSeconds: flight,
    from,
    to,
    distance,
    power,
    band: idealBand(distance, ctx.offense[live.qbSlot]!.ratings.THA, t),
    sep: sim.separation(receiverIndex, throwTime),
    pressure: pressurePenalty(throwTime, live.pocket, t),
  };
}

/** Lobs (low power) hang longer than bullets. */
function ballSpeed(power: number | undefined, distance: number, t: Tunables): number {
  if (power === undefined) return t.throw.ballSpeedYps;
  const th = t.throw;
  const centre = Math.min(th.bandCentreMax, Math.max(th.bandCentreMin, th.bandCentreBase + th.bandCentrePerYard * distance));
  return t.throw.ballSpeedYps * Math.min(1.25, Math.max(0.7, 1 + (power - centre) / 100));
}

/** The catch chance the ring shows while the ball is in the air (no roll). */
export function previewCatch(live: LivePlay, plan: ThrowPlan, timing: CatchTiming): number {
  const { ctx, sim } = live;
  const r = sim.receivers[plan.receiverIndex]!;
  const qb = ctx.offense[live.qbSlot]!;
  const power = plan.power ?? plan.band.centre;
  const quality = throwQuality(qb.ratings.THA, powerAccuracy(power, plan.band, ctx.t), plan.pressure);
  return catchChance(
    {
      airYards: plan.distance,
      wrCth: ctx.offense[r.slot]!.ratings.CTH,
      defCov: ctx.defense[r.defender]!.ratings.COV,
      sep: plan.sep,
      timing,
      powerBonus: powerBonus(power, plan.band, ctx.t),
      catchTypeMod: 0,
      quality,
    },
    ctx.t,
  );
}

/** Rolls the catch when the ball arrives: interception, sail, then catch (GDD section 7). */
export function resolveCatch(live: LivePlay, plan: ThrowPlan, timing: CatchTiming): PassResult {
  const { ctx, sim, rng } = live;
  const r = sim.receivers[plan.receiverIndex]!;
  const wr = ctx.offense[r.slot]!;
  const def = ctx.defense[r.defender]!;
  const qb = ctx.offense[live.qbSlot]!;
  return resolvePass(
    {
      qbTha: qb.ratings.THA,
      wrCth: wr.ratings.CTH,
      defCov: def.ratings.COV,
      sep: plan.sep,
      distanceYards: plan.distance,
      power: plan.power,
      timing,
      pressurePenalty: plan.pressure,
      catchTypeMod: 0,
      aggressive: false,
    },
    rng,
    ctx.t,
  );
}

export type PlayEnd = 'tackled' | 'outOfBounds' | 'touchdown' | 'safety' | 'incomplete' | 'interception' | 'sack' | 'pickSix';

export interface PlayOutcome {
  kind: 'pass' | 'run';
  end: PlayEnd;
  gain: number;
  plan: ThrowPlan | null;
  pass: PassResult | null;
  timing: CatchTiming | null;
  /** Run after catch or run play events (moves, broken tackles, the tackle). */
  events: RacEvent[];
  carrierPos: string;
  update: DriveUpdate;
}

/** Ends a play: applies the gain to the drive. */
export function finishPlay(
  live: LivePlay,
  o: Omit<PlayOutcome, 'update' | 'carrierPos'> & { carrier: Player | null },
): PlayOutcome {
  const intercepted = o.end === 'interception' || o.end === 'pickSix';
  const update = applyPlay(live.ctx.drive, intercepted ? 0 : o.gain, { intercepted }, live.ctx.t);
  return { ...o, carrierPos: o.carrier?.pos ?? '', update };
}

export function headline(o: PlayOutcome): string {
  const u = o.update;
  if (o.end === 'pickSix') return 'PICK SIX';
  if (u.event === 'interception') return 'INTERCEPTED';
  if (u.event === 'touchdown') return 'TOUCHDOWN';
  if (u.event === 'safety') return 'SAFETY';
  const parts: string[] = [];
  const yds = `${o.gain >= 0 ? '+' : ''}${o.gain} YDS`;
  if (o.end === 'sack') parts.push('SACKED', yds);
  else if (o.kind === 'run') parts.push(o.gain >= 10 ? 'BIG RUN' : 'RUN', yds);
  else if (o.end === 'incomplete') parts.push(o.pass?.dropped ? 'DROPPED' : 'INCOMPLETE');
  else parts.push('COMPLETE', yds);
  if (u.event === 'firstDown') parts.push('FIRST DOWN');
  if (u.event === 'turnoverOnDowns') parts.push('TURNOVER ON DOWNS');
  return parts.join(', ');
}

export interface WhyLine {
  label: string;
  value: string;
  tone: 'good' | 'bad' | 'plain';
}

const TIMING_TEXT: Record<CatchTiming, string> = { perfect: 'PERFECT', good: 'GOOD', miss: 'MISSED', none: 'NO TAP' };

/** The "why" rows (GDD 6.9, reference 07): one label and one value each. */
export function whyLines(live: LivePlay, o: PlayOutcome): WhyLine[] {
  const lines: WhyLine[] = [];
  const pct = (n: number) => `${Math.round(n)}%`;
  if (o.end === 'sack') {
    lines.push({ label: 'Pocket', value: `${live.pocket.toFixed(1)}s, held too long`, tone: 'bad' });
    return lines;
  }
  if (o.plan && o.pass) {
    const r = live.sim.receivers[o.plan.receiverIndex]!;
    const wr = live.ctx.offense[r.slot]!;
    const def = live.ctx.defense[r.defender]!;
    const p = o.pass;
    if (o.timing) lines.push({ label: 'Catch timing', value: TIMING_TEXT[o.timing], tone: o.timing === 'perfect' || o.timing === 'good' ? 'good' : 'bad' });
    const inBand = p.power >= p.band.lo && p.power <= p.band.hi;
    lines.push({ label: 'Power', value: `${Math.round(p.power)} (ideal ${Math.round(p.band.lo)} to ${Math.round(p.band.hi)})`, tone: inBand ? 'good' : 'bad' });
    lines.push({ label: 'Matchup', value: `${wr.pos} ${wr.ratings.CTH} vs ${def.pos} ${def.ratings.COV}, ${SHELL_NAMES[live.shell]}`, tone: 'plain' });
    if (o.plan.pressure > 0) lines.push({ label: 'Pressure', value: `-${Math.round(o.plan.pressure * 100)}% accuracy`, tone: 'bad' });
    if (p.outcome === 'interception') {
      lines.push({ label: 'Pick chance', value: `${pct(p.intChance)}, roll ${Math.round(p.intRoll)}: PICKED`, tone: 'bad' });
    } else if (p.sailRoll !== null) {
      lines.push({ label: 'Overthrown', value: `sailed, roll ${Math.round(p.sailRoll)}`, tone: 'bad' });
    } else {
      const caught = p.outcome === 'complete';
      lines.push({ label: 'Catch chance', value: `${pct(p.catchChance)} > ${caught ? 'CAUGHT' : p.dropped ? 'DROPPED' : 'MISSED'}`, tone: caught ? 'good' : 'bad' });
    }
  }
  for (const e of o.events) {
    if (e.kind === 'broke') lines.push({ label: 'Broke a tackle', value: `${pct(e.tackle.chance)}${e.tackle.timing === 'timed' ? ' (timed move)' : ''}`, tone: 'good' });
    if (e.kind === 'tackled') lines.push({ label: 'Tackled', value: `break chance ${pct(e.tackle.chance)}${e.tackle.timing === 'timed' ? ' (timed)' : e.tackle.timing === 'none' ? ' (no move)' : ' (move too early)'}`, tone: 'plain' });
  }
  return lines.slice(0, 5);
}

/** Builds the live run after a catch, a handoff or an interception. Positions are [x0, y0, x1, y1, ...] ball-relative. */
export function startRun(
  live: LivePlay,
  carrierSide: 'offense' | 'defense',
  carrierSlot: number,
  carrierPos: Vec2,
  carrierVel: Vec2,
  offPos: Float32Array,
  defPos: Float32Array,
  opts: { aim: Vec2 | null; auto: boolean; blocking: boolean; tackleAfter?: number },
): RacWorld {
  const { ctx } = live;
  const own = carrierSide === 'offense' ? ctx.offense : ctx.defense;
  const other = carrierSide === 'offense' ? ctx.defense : ctx.offense;
  const ownPos = carrierSide === 'offense' ? offPos : defPos;
  const otherPos = carrierSide === 'offense' ? defPos : offPos;
  // Run plays: the line and tight end block. After a catch or a pick nobody is set to block (AI-concluded).
  const blocks = (p: Player) => opts.blocking && (p.pos === 'OL' || p.pos === 'TE');
  const blockers = own.flatMap((p, i) => (i === carrierSlot || !blocks(p) ? [] : [{ player: p, x: ownPos[i * 2]!, y: ownPos[i * 2 + 1]! }]));
  const defenders = other.map((p, i) => ({ player: p, x: otherPos[i * 2]!, y: otherPos[i * 2 + 1]!, moving: !opts.blocking }));
  return new RacWorld({
    carrier: own[carrierSlot]!,
    carrierPos: { x: carrierPos.x, y: carrierPos.y },
    carrierVel: { x: carrierVel.x, y: carrierVel.y },
    dir: carrierSide === 'offense' ? 1 : -1,
    blockers,
    defenders,
    ballOn: ctx.drive.ballOn,
    aim: opts.aim,
    auto: opts.auto,
    tackleAfter: opts.tackleAfter,
    rng: live.rng,
    t: ctx.t,
  });
}

/** Finite-difference velocity of a receiver on his route. */
export function receiverVelocity(live: LivePlay, index: number, time: number, out: Vec2): Vec2 {
  const a = live.sim.receiverPos(index, time - 0.05, { x: 0, y: 0 });
  const b = live.sim.receiverPos(index, time, { x: 0, y: 0 });
  out.x = (b.x - a.x) / 0.05;
  out.y = (b.y - a.y) / 0.05;
  return out;
}

/**
 * The run after a completed catch. Nobody can tackle him for a beat while the defense closes in and
 * breaks down (a longer beat after a Perfect catch), so there is always time to steer or make a move.
 * time is when the ball arrives; offPos and defPos hold everyone at that moment. auto: the carrier runs himself (bots, tests).
 */
export function startCatchRun(
  live: LivePlay,
  plan: ThrowPlan,
  timing: CatchTiming,
  time: number,
  offPos: Float32Array,
  defPos: Float32Array,
  auto = false,
): RacWorld {
  const r = live.ctx.t.run;
  const vel = receiverVelocity(live, plan.receiverIndex, time, { x: 0, y: 0 });
  vel.x *= r.catchSpeedFactor;
  vel.y *= r.catchSpeedFactor;
  const tackleAfter = r.catchNoTackleSeconds + (timing === 'perfect' ? r.perfectNoTackleBonusSeconds : 0);
  const slot = live.sim.receivers[plan.receiverIndex]!.slot;
  return startRun(live, 'offense', slot, plan.to, vel, offPos, defPos, { aim: null, auto, blocking: false, tackleAfter });
}

/** Where the run play's back takes the ball: beside the QB for a dive, wide for a toss. */
export function handoffPoint(live: LivePlay, out: Vec2): Vec2 {
  const { play, flip } = live.ctx;
  const m = flip ? -1 : 1;
  const rbSlot = play.formation.slots.findIndex((s) => s.pos === 'RB');
  const rb = live.sim.offenseStart[rbSlot]!;
  const qb = live.sim.offenseStart[live.qbSlot]!;
  if (play.run?.toss) {
    out.x = rb.x + m * 3;
    out.y = rb.y - 0.5;
  } else {
    out.x = qb.x + m * 0.6;
    out.y = qb.y + 0.6;
  }
  return out;
}

/**
 * Defenders break on the ball once it is in the air: after a short read, every defender who is not
 * rushing runs at his speed toward the catch point, stopping a little short. Writes into defPos.
 */
export function breakOnBall(live: LivePlay, plan: ThrowPlan, time: number, defPos: Float32Array, startPos: Float32Array): void {
  const { ctx } = live;
  const t = ctx.t;
  const run = Math.max(0, time - plan.throwTime - t.run.reactionSeconds);
  if (run <= 0) return;
  for (let j = 0; j < ctx.defense.length; j++) {
    const p = ctx.defense[j]!;
    if (p.pos === 'DL') continue;
    const sx = startPos[j * 2]!;
    const sy = startPos[j * 2 + 1]!;
    const ox = defPos[j * 2]!;
    const oy = defPos[j * 2 + 1]!;
    const dx = plan.to.x - sx;
    const dy = plan.to.y - sy;
    const dist = Math.hypot(dx, dy);
    const reach = Math.max(0, dist - t.run.contactRadiusYards * 1.5);
    const go = Math.min(reach, (t.movement.baseSpeedYps + t.movement.perSpdYps * p.ratings.SPD) * run * t.run.pursuitSpeedFactor);
    if (dist < 0.01) continue;
    // Blend from the coverage position toward the ball line so it reads as a break, not a teleport.
    const bx = sx + (dx / dist) * go;
    const by = sy + (dy / dist) * go;
    const w = Math.min(1, run / 0.4);
    defPos[j * 2] = ox + (bx - ox) * w;
    defPos[j * 2 + 1] = oy + (by - oy) * w;
  }
}
